import { useEffect, useState } from 'react';
import { isDemoMode } from '../../config/env';
import { dancerEmoji, type Honorific, type PersonVisual } from '../../lib/honorific';
import { api } from '../../services/api';
import './uiAtoms.css';

// Module-level caches to prevent redundant fetching, avoid flashing, and persist object URLs
const avatarBlobCache = new Map<string, string | null>();
const inFlightAvatarRequests = new Map<string, Promise<string | null>>();

export function setCachedAvatar(userId: string, url: string | null) {
  avatarBlobCache.set(userId, url);
}

function getDemoAvatar(userId: string): string | null {
  try {
    const raw = sessionStorage.getItem('dancemance_demo_state');
    if (raw) {
      const parsed = JSON.parse(raw);
      const user = parsed?.people?.find((p: any) => p._id === userId || p.id === userId);
      if (user?.photoUrl) return user.photoUrl;
    }
  } catch {}
  return null;
}

export function PersonAvatar({
  name,
  honorific,
  photoFileId,
  photoUrl,
  userId,
  color = '#8B6F47',
  size = 'md',
}: {
  name: string;
  honorific?: Honorific | null;
  photoFileId?: string | null;
  photoUrl?: string | null;
  userId?: string;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [src, setSrc] = useState<string | null>(() => {
    if (photoUrl) return photoUrl;
    if (userId && avatarBlobCache.has(userId)) {
      return avatarBlobCache.get(userId) ?? null;
    }
    if (userId && isDemoMode) {
      return getDemoAvatar(userId);
    }
    return null;
  });

  useEffect(() => {
    if (photoUrl) {
      setSrc(photoUrl);
      return;
    }

    if (!userId) {
      setSrc(null);
      return;
    }

    if (avatarBlobCache.has(userId)) {
      setSrc(avatarBlobCache.get(userId) ?? null);
      return;
    }

    if (isDemoMode) {
      const demoPhoto = getDemoAvatar(userId);
      avatarBlobCache.set(userId, demoPhoto);
      setSrc(demoPhoto);
      return;
    }

    // In live mode: if photoFileId is explicitly null, don't query
    if (photoFileId === null) {
      avatarBlobCache.set(userId, null);
      setSrc(null);
      return;
    }

    let alive = true;
    let req = inFlightAvatarRequests.get(userId);
    if (!req) {
      req = api
        .getBlob(`/users/${userId}/photo`)
        .then((blobUrl) => {
          avatarBlobCache.set(userId, blobUrl);
          return blobUrl;
        })
        .catch(() => {
          avatarBlobCache.set(userId, null);
          return null;
        })
        .finally(() => {
          inFlightAvatarRequests.delete(userId);
        });
      inFlightAvatarRequests.set(userId, req);
    }

    req.then((url) => {
      if (alive) {
        setSrc(url);
      }
    });

    return () => {
      alive = false;
    };
  }, [photoUrl, photoFileId, userId]);

  const emoji = dancerEmoji(honorific);

  return (
    <span
      className={`ui-avatar ui-avatar--${size} ${src ? 'ui-avatar--photo' : ''} ${src ? '' : 'ui-avatar--emoji'}`}
      style={src ? undefined : { background: `${color}18`, color }}
      aria-label={name}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          onError={() => {
            setSrc(null);
            if (userId) avatarBlobCache.set(userId, null);
          }}
        />
      ) : (
        emoji
      )}
    </span>
  );
}

export function PersonStack({ people, color }: { people: PersonVisual[]; color?: string }) {
  const shown = people.slice(0, 3);
  if (shown.length === 0) return null;
  return (
    <span className="person-stack" aria-hidden="true">
      {shown.map((person, index) => {
        const uid = person._id ?? person.id ?? person.userId;
        return (
          <PersonAvatar
            key={uid ?? `${person.name}-${index}`}
            name={person.name}
            honorific={person.honorific}
            photoFileId={person.photoFileId}
            photoUrl={person.photoUrl}
            userId={uid}
            color={color}
            size="sm"
          />
        );
      })}
    </span>
  );
}
